import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Dropdown from 'flarum/common/components/Dropdown';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import EngineVersion from '../../common/models/EngineVersion';
import type Mithril from 'mithril';

interface EngineVersionDropdownAttrs {
  selectedVersion: EngineVersion | null;
  onselect: (version: EngineVersion) => void;
}

// 标记是否已完整加载过所有引擎版本
let allVersionsLoaded = false;

export default class EngineVersionDropdown extends Component<EngineVersionDropdownAttrs> {
  loading: boolean = false;

  oninit(vnode: Mithril.Vnode<EngineVersionDropdownAttrs, this>) {
    super.oninit(vnode);

    // 每次都从 API 加载完整的引擎版本列表
    // 因为 store 中可能只有部分数据（从帖子列表 include 加载的）
    if (!allVersionsLoaded) {
      this.loading = true;
      app.store.find('engine-versions').then(() => {
        this.loading = false;
        allVersionsLoaded = true;
        // 加载完成后，如果没有选中的版本，默认选中第一个
        this.selectDefaultIfNeeded();
        m.redraw();
      }).catch(() => {
        this.loading = false;
        m.redraw();
      });
    } else {
      // 如果已加载过完整数据，直接使用
      this.selectDefaultIfNeeded();
    }
  }

  getVersionsSorted(): EngineVersion[] {
    const versions = app.store.all<EngineVersion>('engine-versions');
    return versions.sort((a, b) => (a.position() || 0) - (b.position() || 0));
  }

  selectDefaultIfNeeded() {
    const { selectedVersion, onselect } = this.attrs;
    if (!selectedVersion) {
      const versions = this.getVersionsSorted();
      if (versions.length > 0) {
        onselect(versions[0]);
      }
    }
  }

  view(): Mithril.Children {
    const { selectedVersion, onselect } = this.attrs;
    const versions = this.getVersionsSorted();

    const label = selectedVersion
      ? selectedVersion.name()
      : app.translator.trans('laya-engine-version.forum.composer.choose_version');

    const buttonStyle = selectedVersion
      ? {
          backgroundColor: selectedVersion.color(),
          color: '#fff',
          borderColor: selectedVersion.color(),
        }
      : {};

    // 如果正在加载，显示加载状态
    if (this.loading) {
      return m(
        'button.Button.EngineVersionDropdown-button.disabled',
        { disabled: true },
        [
          m(LoadingIndicator, { size: 'small', display: 'inline' }),
          ' ',
          app.translator.trans('laya-engine-version.forum.composer.loading'),
        ]
      );
    }

    return m(
      Dropdown,
      {
        buttonClassName: 'Button EngineVersionDropdown-button',
        menuClassName: 'Dropdown-menu--right EngineVersionDropdown-menu',
        label: label,
        icon: 'fas fa-code-branch',
        buttonAttrs: { style: buttonStyle },
      },
      versions.map((version) =>
        m(
          Button,
          {
            className: 'Button EngineVersionDropdown-item',
            onclick: () => onselect(version),
            icon: selectedVersion && selectedVersion.id() === version.id() ? 'fas fa-check' : '',
          },
          [
            m('span', {
              className: 'EngineVersionDropdown-color',
              style: { backgroundColor: version.color() },
            }),
            version.name(),
          ]
        )
      )
    );
  }
}
